import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import Icon, { IconTypes } from "shared/ui/icon";

import { zodResolver } from "@hookform/resolvers/zod";
import { useVoucherCartAddition } from "app/hooks/shared/useVoucherCartAddition";
import { SubmitHandler, useForm } from "react-hook-form";

import { z } from "zod";
import TextInput from "../../checkout/send-to-email/components/Textinput";
import { TextInputFormFields } from "../TextInputFormFields";
import { VoucherButton } from "./components/VoucherButton";
import { VoucherHeader } from "./components/VoucherHeader";
import { useCartContext } from "app/providers/CartProvider";
import { motion } from "framer-motion";

export const voucherBtnVariants = {
  shown: {
    opacity: 1,
    transition: { duration: 0.25, ease: "easeInOut" },
  },
  hidden: {
    opacity: 0,
    transition: { duration: 0.25, ease: "easeInOut" },
  },
};

const validationSchema = z.object({
  Voucher: z.string().min(1, { message: "Voucher is verplicht" }),
});

export default function VoucherModal({ orderType }: { orderType: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TextInputFormFields>({
    resolver: zodResolver(validationSchema),
  });

  const { addVoucherToCart, status, resetAddVoucherToCart } =
    useVoucherCartAddition();
  const { cartHasDiscountCode } = useCartContext();

  // Handle form submission
  const onSubmit: SubmitHandler<TextInputFormFields> = async (data) => {
    // Call the addVoucherToCart function with the voucher data
    const success = await addVoucherToCart(data.Voucher, orderType);
    // If the voucher was added successfully, close the modal
    if (success) {
      onModalClose();
      // Reset the form fields
      reset();
    }
  };

  // Handle modal close
  const onModalClose = () => {
    // Close the modal by setting isOpen to false
    setIsOpen(false);

    // Reset the status of the addVoucherToCart function
    resetAddVoucherToCart();
  };

  return (
    <>
      <motion.button
        initial={false}
        variants={voucherBtnVariants}
        animate={!cartHasDiscountCode ? "shown" : "hidden"}
        style={{ pointerEvents: !cartHasDiscountCode ? "auto" : "none" }}
        aria-hidden={cartHasDiscountCode}
        tabIndex={!cartHasDiscountCode ? 0 : -1}
        onClick={() => setIsOpen(true)}
      >
        <h5 className="gg-flex gg-whitespace-nowrap gg-text-yellow gg-underline hover:gg-text-yellow-400 hover:gg-underline">
          <Icon name={IconTypes.DISCOUNT} className="gg-mr-2.5" />
          Voeg voucher toe
        </h5>
      </motion.button>

      <Transition as={Dialog} show={isOpen} onClose={onModalClose}>
        <Transition.Child
          as={Fragment}
          enter="gg-ease-out gg-duration-300"
          enterFrom="gg-opacity-0 gg-scale-100"
          enterTo="gg-opacity-100 gg-scale-100"
          leave="gg-ease-in gg-duration-100"
          leaveFrom="gg-opacity-100 gg-scale-100"
          leaveTo="gg-opacity-0 gg-scale-95"
        >
          <div className="gg-fixed gg-left-0 gg-top-0 gg-z-50 gg-grid gg-h-full gg-w-screen gg-place-items-center gg-overflow-x-hidden gg-backdrop-blur-md lg:gg-inset-0 lg:gg-h-auto">
            <Dialog.Panel className="gg-grid gg-h-fit gg-w-full gg-grid-cols-9 gg-rounded gg-bg-white gg-shadow-lg sm:gg-max-w-md lg:gg-max-w-lg">
              {/* Header */}
              <VoucherHeader onModalClose={onModalClose} />

              {/* Inputs */}
              <form
                id="sendVoucherForm"
                onSubmit={handleSubmit(onSubmit)}
                className="gg-col-span-9 gg-px-5 gg-pb-8 gg-pt-4 sm:gg-px-6 lg:gg-px-8 lg:gg-pb-12"
              >
                <TextInput
                  register={register}
                  id="voucher"
                  label="Voucher"
                  ariaLabel="Voucher"
                  errors={errors}
                  showLabel={false}
                  inputClassName="!gg-w-auto"
                />
              </form>
              {/* Inputs end */}
              {/* Button */}
              <div className="gg-col-span-9 gg-flex gg-justify-end gg-border-t gg-border-gray-200 gg-px-6 gg-pb-6 gg-pt-5 lg:gg-px-8 lg:gg-pb-8">
                <VoucherButton formName="sendVoucherForm" status={status} />
              </div>
              {/* Button */}
            </Dialog.Panel>
          </div>
        </Transition.Child>
      </Transition>
    </>
  );
}
